import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import { dictionary } from '../../../dictionary';
import InvoiceConversion from './InvoiceConversion';
import InvoiceCosts from './InvoiceCosts';
import InvoicePay from './InvoicePay';
import InvoiceNrPayment from './InvoiceNrPayment';
import InvoiceConfirm from './InvoiceConfirm';
import InvoiceModuleStages from './InvoiceModuleStages';
import InvoiceCompletion from './InvoiceCompletion';
import InvoiceFooter from './InvoiceFooter';
import ObjectID from 'bson-objectid';
import { fetchInvPosts } from '../../../redux/thunk/thunk';
import { useDispatch } from 'react-redux';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_URL = isDevelopment
  ? process.env.REACT_APP_API_URL
  : 'https://eld-timer-backend-service.api.prod.sit.sys.odj.cloud/';

const InvoiceDialog = ({
  showInvoice,
  setShowInvoice,
  invoiceData,
  setInvoiceData,
  invEdit,
  setInvEdit,
  showToast,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const possibleStages = ['ELD0', 'ELD1', 'ELD2', 'ELD3', 'ELD4', 'ELD5', 'Additional changes', 'Sign_off'];
  const stages = invoiceData?.entries
    ? [...new Set(invoiceData.entries.map((entry) => (entry.stage ? entry.stage.trim() : '')))].sort()
    : [];

  const initialState = {
    conversion: invoiceData?.conv ? invoiceData.conv : 4.2131,
    startStage: invoiceData?.startStage ? invoiceData.startStage : stages[0],

    espCostNet: invoiceData?.espCostNet || 0,
    espCostGross: invoiceData?.espCostGross || 0,
    espCostNetEUR: invoiceData?.espCostNetEUR || 0,
    espCostGrossEUR: invoiceData?.espCostGrossEUR || 0,
    espCostPrcEUR: invoiceData?.espCostPrcEUR ?? 484,
    espCostTotal: invoiceData?.espCostTotal || 0,

    dateInvoice1: invoiceData?.dateInvoice1 ? new Date(invoiceData.dateInvoice1) : null,
    dateInvoice2: invoiceData?.dateInvoice2 ? new Date(invoiceData.dateInvoice2) : null,

    netPaid1Invoice: invoiceData?.netPaid1Invoice || 0,
    netPaid2Invoice: invoiceData?.netPaid2Invoice || 0,
    netPaidEUR1Invoice: invoiceData?.netPaidEUR1Invoice || 0,
    netPaidEUR2Invoice: invoiceData?.netPaidEUR2Invoice || 0,
    grossPaid1Invoice: invoiceData?.grossPaid1Invoice || 0,
    grossPaid2Invoice: invoiceData?.grossPaid2Invoice || 0,
    grossPaidEUR1Invoice: invoiceData?.grossPaidEUR1Invoice || 0,
    grossPaidEUR2Invoice: invoiceData?.grossPaidEUR2Invoice || 0,

    netPaid: invoiceData?.netPaid || 0,
    netPaidEUR: invoiceData?.netPaidEUR || 0,
    grossPaid: invoiceData?.grossPaid || 0,
    grossPaidEUR: invoiceData?.grossPaidEUR || 0,
    netPayable: invoiceData?.netPayable || 0,
    netPayableEUR: invoiceData?.netPayableEUR || 0,
    grossPayable: invoiceData?.grossPayable || 0,
    grossPayableEUR: invoiceData?.grossPayableEUR || 0,

    invoiceNumber1: invoiceData?.invoiceNumber1 || '',
    payment1Confirmed: invoiceData?.payment1Confirmed ? new Date(invoiceData.payment1Confirmed) : null,
    invoiceNumber2: invoiceData?.invoiceNumber2 || '',
    payment2Confirmed: invoiceData?.payment2Confirmed ? new Date(invoiceData.payment2Confirmed) : null,

    completionMonthYear: invoiceData?.completionMonthYear ? new Date(invoiceData.completionMonthYear) : new Date(),
    invoiceStatus: invoiceData?.invoiceStatus || 'incomplete',
    comment: invoiceData?.comment || '',
  };

  const [state, setState] = useState(initialState);
  const INVOICE_ID = invoiceData?._id;

  const options = [
    {
      value: 'incomplete',
      label: dictionary.esp_dialog_invoice_check_incomplete,
      icon: 'pi pi-file-edit',
      className: 'status-red',
    },
    {
      value: 'in-progress',
      label: dictionary.esp_dialog_invoice_check_inprogress,
      icon: 'pi pi-exclamation-triangle',
      className: 'status-yellow',
    },
    {
      value: 'complete',
      label: dictionary.esp_dialog_invoice_check_complete,
      icon: 'pi pi-check-circle',
      className: 'status-green',
    },
    {
      value: 'withdrawn',
      label: dictionary.esp_dialog_invoice_check_withdrawn,
      icon: 'pi pi-times',
      className: 'status-blue',
    },
  ];

  const handleHide = () => {
    setShowInvoice(false);
    setInvoiceData(null);
    setInvEdit(false);
    setLoading(false);
  };

  const handleUpdateInvoice = async (id, overrideStatus) => {
    setLoading(true);
    const payload = preparePayload(id, overrideStatus);

    try {
      const response = await fetch(`${API_URL}invoices/${id}`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Update failed');

      await dispatch(fetchInvPosts());
      showToast('success', dictionary.esp_dialog_invoice_save_success);
      handleHide();
    } catch (error) {
      console.error(error);
      showToast('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const preparePayload = (id, overrideStatus) => ({
    id: id,
    entries: invoiceData.entries,
    startStage: state.startStage,
    conversion: state.conversion,
    espCostNet: state.espCostNet,
    espCostGross: state.espCostGross,
    espCostNetEUR: state.espCostNetEUR,
    espCostGrossEUR: state.espCostGrossEUR,
    espCostPrcEUR: state.espCostPrcEUR,
    espCostTotal: state.espCostTotal,
    dateInvoice1: state.dateInvoice1 ? state.dateInvoice1.toISOString() : '',
    dateInvoice2: state.dateInvoice2 ? state.dateInvoice2.toISOString() : '',
    netPaid1Invoice: state.netPaid1Invoice,
    netPaid2Invoice: state.netPaid2Invoice,
    netPaidEUR1Invoice: state.netPaidEUR1Invoice,
    netPaidEUR2Invoice: state.netPaidEUR2Invoice,
    grossPaid1Invoice: state.grossPaid1Invoice,
    grossPaid2Invoice: state.grossPaid2Invoice,
    grossPaidEUR1Invoice: state.grossPaidEUR1Invoice,
    grossPaidEUR2Invoice: state.grossPaidEUR2Invoice,
    netPaid: state.netPaid,
    netPaidEUR: state.netPaidEUR,
    grossPaid: state.grossPaid,
    grossPaidEUR: state.grossPaidEUR,
    netPayable: state.netPayable,
    netPayableEUR: state.netPayableEUR,
    grossPayable: state.grossPayable,
    grossPayableEUR: state.grossPayableEUR,
    invoiceNumber1: state.invoiceNumber1,
    payment1Confirmed: state.payment1Confirmed ? state.payment1Confirmed.toISOString() : '',
    invoiceNumber2: state.invoiceNumber2,
    payment2Confirmed: state.payment2Confirmed ? state.payment2Confirmed.toISOString() : '',
    completionMonthYear: state.completionMonthYear ? state.completionMonthYear.toISOString() : '',
    invoiceStatus: overrideStatus || state.invoiceStatus,
    comment: state.comment,
  });

  const handleSaveInvoice = async (overrideStatus) => {
    if (invEdit) {
      await handleUpdateInvoice(INVOICE_ID, overrideStatus);
      return;
    }

    setLoading(true);
    const payload = preparePayload(ObjectID().toHexString(), overrideStatus);

    try {
      const response = await fetch(`${API_URL}invoices`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Creation failed');

      await dispatch(fetchInvPosts());
      showToast('success', dictionary.esp_dialog_invoice_save_success);
      handleHide();
    } catch (error) {
      console.error(error);
      showToast('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (invoiceData) {
      setState(initialState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData]);

  return (
    <>
      <Dialog
        header={dictionary.esp_dialog_invoice_header}
        visible={showInvoice}
        style={{ width: '50%' }}
        onHide={handleHide}
        className="invoice-dialog"
      >
        {loading && (
          <div className="loading-wrapper">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="2" animationDuration="3s" />
          </div>
        )}
        <InvoiceConversion
          conversion={state.conversion}
          setConversion={(val) => setState((prev) => ({ ...prev, conversion: val }))}
          startStage={state.startStage}
          setStartStage={(val) => setState((prev) => ({ ...prev, startStage: val }))}
          possibleStages={possibleStages}
        />
        <Divider />
        <InvoiceCosts
          conversion={state.conversion}
          espCostNet={state.espCostNet}
          setEspCostNet={(val) => setState((prev) => ({ ...prev, espCostNet: val }))}
          espCostGross={state.espCostGross}
          setEspCostGross={(val) => setState((prev) => ({ ...prev, espCostGross: val }))}
          espCostNetEUR={state.espCostNetEUR}
          setEspCostNetEUR={(val) => setState((prev) => ({ ...prev, espCostNetEUR: val }))}
          espCostGrossEUR={state.espCostGrossEUR}
          setEspCostGrossEUR={(val) => setState((prev) => ({ ...prev, espCostGrossEUR: val }))}
          espCostPrcEUR={state.espCostPrcEUR}
          setEspCostPrcEUR={(val) => setState((prev) => ({ ...prev, espCostPrcEUR: val }))}
          espCostTotal={state.espCostTotal}
          setEspCostTotal={(val) => setState((prev) => ({ ...prev, espCostTotal: val }))}
          netPaid1Invoice={state.netPaid1Invoice}
          netPaid2Invoice={state.netPaid2Invoice}
        />
        <Divider />
        <InvoiceNrPayment
          conversion={state.conversion}
          netPaid1Invoice={state.netPaid1Invoice}
          setNetPaid1Invoice={(val) => setState((prev) => ({ ...prev, netPaid1Invoice: val }))}
          netPaid2Invoice={state.netPaid2Invoice}
          setNetPaid2Invoice={(val) => setState((prev) => ({ ...prev, netPaid2Invoice: val }))}
          netPaidEUR1Invoice={state.netPaidEUR1Invoice}
          setNetPaidEUR1Invoice={(val) => setState((prev) => ({ ...prev, netPaidEUR1Invoice: val }))}
          netPaidEUR2Invoice={state.netPaidEUR2Invoice}
          setNetPaidEUR2Invoice={(val) => setState((prev) => ({ ...prev, netPaidEUR2Invoice: val }))}
          grossPaid1Invoice={state.grossPaid1Invoice}
          setGrossPaid1Invoice={(val) => setState((prev) => ({ ...prev, grossPaid1Invoice: val }))}
          grossPaid2Invoice={state.grossPaid2Invoice}
          setGrossPaid2Invoice={(val) => setState((prev) => ({ ...prev, grossPaid2Invoice: val }))}
          grossPaidEUR1Invoice={state.grossPaidEUR1Invoice}
          setGrossPaidEUR1Invoice={(val) => setState((prev) => ({ ...prev, grossPaidEUR1Invoice: val }))}
          grossPaidEUR2Invoice={state.grossPaidEUR2Invoice}
          setGrossPaidEUR2Invoice={(val) => setState((prev) => ({ ...prev, grossPaidEUR2Invoice: val }))}
          dateInvoice1={state.dateInvoice1}
          setDateInvoice1={(val) => setState((prev) => ({ ...prev, dateInvoice1: val }))}
          dateInvoice2={state.dateInvoice2}
          setDateInvoice2={(val) => setState((prev) => ({ ...prev, dateInvoice2: val }))}
        />
        <Divider />
        <InvoiceConfirm
          invoiceNumber1={state.invoiceNumber1}
          setInvoiceNumber1={(val) => setState((prev) => ({ ...prev, invoiceNumber1: val }))}
          payment1Confirmed={state.payment1Confirmed}
          setPayment1Confirmed={(val) => setState((prev) => ({ ...prev, payment1Confirmed: val }))}
          invoiceNumber2={state.invoiceNumber2}
          setInvoiceNumber2={(val) => setState((prev) => ({ ...prev, invoiceNumber2: val }))}
          payment2Confirmed={state.payment2Confirmed}
          setPayment2Confirmed={(val) => setState((prev) => ({ ...prev, payment2Confirmed: val }))}
        />
        <Divider />
        <InvoicePay
          conversion={state.conversion}
          espCostNet={state.espCostNet}
          netPaid1Invoice={state.netPaid1Invoice}
          netPaid2Invoice={state.netPaid2Invoice}
          netPaid={state.netPaid}
          setNetPaid={(val) => setState((prev) => ({ ...prev, netPaid: val }))}
          netPaidEUR={state.netPaidEUR}
          setNetPaidEUR={(val) => setState((prev) => ({ ...prev, netPaidEUR: val }))}
          grossPaid={state.grossPaid}
          setGrossPaid={(val) => setState((prev) => ({ ...prev, grossPaid: val }))}
          grossPaidEUR={state.grossPaidEUR}
          setGrossPaidEUR={(val) => setState((prev) => ({ ...prev, grossPaidEUR: val }))}
          netPayable={state.netPayable}
          setNetPayable={(val) => setState((prev) => ({ ...prev, netPayable: val }))}
          netPayableEUR={state.netPayableEUR}
          setNetPayableEUR={(val) => setState((prev) => ({ ...prev, netPayableEUR: val }))}
          grossPayable={state.grossPayable}
          setGrossPayable={(val) => setState((prev) => ({ ...prev, grossPayable: val }))}
          grossPayableEUR={state.grossPayableEUR}
          setGrossPayableEUR={(val) => setState((prev) => ({ ...prev, grossPayableEUR: val }))}
        />
        <Divider />
        <InvoiceCompletion
          completionMonthYear={state.completionMonthYear}
          setCompletionMonthYear={(val) => setState((prev) => ({ ...prev, completionMonthYear: val }))}
        />
        <Divider />
        <InvoiceModuleStages stages={stages} possibleStages={possibleStages} />
        <Divider />
        <InvoiceFooter
          comment={state.comment}
          setComment={(val) => setState((prev) => ({ ...prev, comment: val }))}
          invEdit={invEdit}
          handleSaveInvoice={handleSaveInvoice}
          invoiceStatus={state.invoiceStatus}
          setInvoiceStatus={(val) => setState((prev) => ({ ...prev, invoiceStatus: val }))}
          options={options}
        />
      </Dialog>
    </>
  );
};

export default InvoiceDialog;
